import React, { Component } from "react";
import { Container, Alert } from "reactstrap";
import { RouterProps, Styled } from "../../../../toolkit/decorators/Artifact";
import s from "./styles.scss";
import { downloadReporteSemestral } from "../../../actions/comisionAnualidad";
import { ProcesoSemestralDispatcher } from "../../../containers/comisionesAnualidad/procesoSemestral/ProcesoSemestral.dispatcher";
import { ProcesoSemestralStore } from "../../../containers/comisionesAnualidad/procesoSemestral/ProcesoSemestral.store";
import FormBuilder from "../../../../toolkit/baseForms2/formBuilder/FormBuilder";
import ISelectBuilder from "../../../../toolkit/baseForms2/iSelectBuilder/ISelectBuilder";
import { Inputs, Buttons, formConfig } from "./ProcesoSemestral.form";
import {
  errorNotification,
  successNotification
} from "../../../../utils/notifications";
import ConfirmModal from "../../common/confirmModal/confirmModal";
import { formatPeriodo } from "../../../helpers/formatters";

interface Props
  extends ProcesoSemestralStore,
    ProcesoSemestralDispatcher,
    RouterProps {}

interface State {
  downloading: boolean;
  showConfirmModal: boolean;
  pendingValues: {
    operacion: string;
    periodo: string;
    metaChater?: number;
  } | null;
  pollingIntervalId: number | null;
  lastPollingTime: number;
}

@Styled(s)
export default class ProcesoSemestral extends Component<Props, State> {
  state: State = {
    downloading: false,
    showConfirmModal: false,
    pendingValues: null,
    pollingIntervalId: null,
    lastPollingTime: 0
  };

  async componentDidMount() {
    await this.props.getProyectosOptions();
    await this.props.getCalculoSemestralAceptable();
    this.startPolling();
  }

  componentDidUpdate(prevProps: Props) {
    // Verificar si cambió el estado de calculoSemestralAceptable
    if (
      prevProps.calculoSemestralAceptable !==
      this.props.calculoSemestralAceptable
    ) {
      if (!this.props.calculoSemestralAceptable) {
        // Hay un cálculo en ejecución, iniciar polling
        this.startPolling();
      } else {
        // No hay cálculo en ejecución, detener polling
        this.stopPolling();
      }
    }
  }

  componentWillUnmount() {
    this.stopPolling();
  }

  startPolling = () => {
    // Evitar múltiples intervalos
    if (this.state.pollingIntervalId) {
      return;
    }

    const intervalId = setInterval(() => {
      const now = Date.now();
      const timeSinceLastPoll = now - this.state.lastPollingTime;

      // Solo hacer polling si han pasado al menos 30 segundos desde la última comprobación
      if (timeSinceLastPoll >= 30000 || this.state.lastPollingTime === 0) {
        this.setState({ lastPollingTime: now });
        this.props.getCalculoSemestralAceptable();
      }
    }, 30000) as any; // Cada 30 segundos

    this.setState({ pollingIntervalId: intervalId });
  };

  stopPolling = () => {
    if (this.state.pollingIntervalId) {
      clearInterval(this.state.pollingIntervalId);
      this.setState({ pollingIntervalId: null });
    }
  };

  submit = async (values: {
    operacion: string;
    periodo: string;
    metaChater?: number;
  }) => {
    const { operacion, periodo, metaChater } = values;

    if (operacion === "DESCARGAR") {
      this.setState({ downloading: true });

      downloadReporteSemestral(periodo, () => {
        this.setState({ downloading: false });
      });
    } else {
      // Mostrar modal de confirmación antes de calcular
      this.setState({
        showConfirmModal: true,
        pendingValues: { operacion, periodo, metaChater }
      });
    }
  };

  handleConfirmCalcular = async () => {
    this.setState({ showConfirmModal: false });

    const { pendingValues } = this.state;
    const { periodo, metaChater } = pendingValues;
    const metaChaterValue = Number(metaChater);

    const preview = await this.props.postCalcularComisionesSemestral(
      periodo,
      metaChaterValue
    );

    if (preview.fulfilled) {
      successNotification(
        "Cálculo de comisiones semestrales iniciado en segundo plano"
      );

      await this.props.getCalculoSemestralAceptable();
    } else {
      errorNotification(preview.postingCalcularComisionesSemestralError);
    }

    this.setState({ pendingValues: null });
  };

  handleCancelCalcular = () => {
    this.setState({
      showConfirmModal: false,
      pendingValues: null
    });
  };

  render() {
    const {
      anualidadProyectosOptions,
      gettingAnualidadProyectosOptions,
      postingCalcularComisionesSemestral
    } = this.props;

    const { calculoSemestralAceptable } = this.props;

    const loading =
      gettingAnualidadProyectosOptions ||
      postingCalcularComisionesSemestral ||
      this.state.downloading;

    // Deshabilitar formulario si hay un cálculo en ejecución
    const formDisabled = !calculoSemestralAceptable || loading;

    const pendingValues = this.state.pendingValues || ({} as any);

    return (
      <Container>
        {!calculoSemestralAceptable && (
          <Alert color="warning" className="mt-3 mb-3">
            Actualmente hay un cálculo semestral en proceso.
          </Alert>
        )}

        <div className="ml-3 mr-3">
          <FormBuilder<Inputs, Buttons>
            config={formConfig}
            submit={this.submit}
            processing={!calculoSemestralAceptable}
            loading={formDisabled}
          >
            <ISelectBuilder
              name="periodo"
              data={anualidadProyectosOptions || []}
            />
          </FormBuilder>
        </div>

        <ConfirmModal
          isOpen={this.state.showConfirmModal}
          title="Confirmar cálculo de comisiones"
          message={`¿Está seguro que desea iniciar el cálculo de comisiones semestrales para el Proyecto ${formatPeriodo(
            pendingValues.periodo
          )}?\n\nMeta Chater: ${
            pendingValues.metaChater
          }\n\nEsta operación puede tardar varios minutos.`}
          requireInput={false}
          inputType=""
          inputTitle=""
          waiting={postingCalcularComisionesSemestral}
          options={[]}
          size="md"
          currentValue={null}
          okTitle="Sí, calcular"
          cancelTitle="Cancelar"
          style={{}}
          ok={this.handleConfirmCalcular}
          cancel={this.handleCancelCalcular}
        />
      </Container>
    );
  }
}
